/**
 * WORKAROUND: Addison Lee forced checkout redirect (shop 87510352207).
 * Also (Part 3, further down): relabels the basket button "Local Delivery".
 *
 * The problem
 * Addison Lee's widget (<pre-purchase-form>, loaded by the Addison Lee app embed)
 * contains custom code for this shop ("forceCheckoutButton"). It listens for clicks
 * on the basket drawer and the checkout buttons, cancels the normal Checkout click
 * and jumps straight to /checkout. Because the basket form never submits, Pickeasy
 * cannot save the customer's choice (collection, date, slot) and checkout shows
 * "Shipping not available". Found and confirmed 8 Oct 2026.
 *
 * The fix
 * Addison Lee's listeners look up this.checkoutClickHandler(e) at the moment of the
 * click, and the widget re-creates that function several times. So instead of trying
 * to remove the listeners (they are anonymous and keep multiplying), we make the
 * checkoutClickHandler property always return a do-nothing function and ignore any
 * attempt to replace it. The click then carries on as a normal form submit, so
 * Pickeasy and Easify run as usual. Addison Lee's own booking is already saved in
 * the basket before checkout, so nothing of theirs is lost.
 *
 * Safety
 * - Sends no requests and removes nothing. It only changes what one function does.
 * - If the Addison Lee embed is switched off (as on the live site), the widget is
 *   never defined and this file does nothing.
 * - It depends on Addison Lee's internal name "checkoutClickHandler". If they update
 *   their script and rename it, this silently stops working and collection breaks
 *   again with the embed on. Re-test collection with Addison Lee on after any update.
 *
 * Why Addison Lee had the redirect
 * Pickeasy silently blocks the Checkout click when no Pickeasy date is chosen. A
 * customer who books Addison Lee hasn't chosen a Pickeasy date, so without the
 * redirect they can't get to checkout at all. Part 2 below handles that case only.
 *
 * Remove this file (and its <script> tag in layout/theme.liquid) if Addison Lee
 * switch the forced redirect off for this shop and the apps work together without it.
 */
(function () {
  const noop = () => {};

  // Turns checkoutClickHandler on the given object into a fixed do-nothing function.
  const neutralise = (target) => {
    Object.defineProperty(target, 'checkoutClickHandler', {
      configurable: true,
      get: () => noop,
      set: () => {}, // ignore Addison Lee re-assigning it
    });
  };

  if (!window.customElements) return;

  customElements.whenDefined('pre-purchase-form').then(() => {
    // Widgets created from now on: Addison Lee's "this.checkoutClickHandler = …"
    // hits the setter on the prototype and is ignored.
    neutralise(customElements.get('pre-purchase-form').prototype);

    // Widgets already on the page may already hold their own copy, so fix those too.
    document.querySelectorAll('pre-purchase-form').forEach(neutralise);

    // Part 2 only applies when the Addison Lee widget exists (embed switched on).
    listenForBlockedCourierCheckout();

    // Part 3: show "Local Delivery" on the basket button instead of "Addison Lee Delivery".
    renameCourierButton();
  });

  /**
   * Part 3: button wording (agreed with Mike, 21 Oct 2026).
   *
   * The resting label of the Addison Lee button is a span in the app's own markup.
   * Their script only shows/hides these spans and never rewrites the text, and it
   * keeps re-using the same button (see restoreCourierButton in cart-drawer.js), so
   * changing the text once on page load lasts through every basket update.
   * The real text is changed (not a CSS trick) so screen readers say it too.
   *
   * Not changed: "Timed delivery for [date] | [time]" shown after a booking, the
   * booking window itself, and the rate name at checkout (that comes from Addison
   * Lee's shipping service, not the theme).
   * If Addison Lee rename these spans in an update, the button simply goes back to
   * their wording; nothing breaks.
   */
  function renameCourierButton() {
    const labels = {
      '.pre-purchase-modal__button-no-selection': 'Local Delivery',
      '.pre-purchase-modal__button-product-tag-invalid': 'Local Delivery unavailable for your cart items',
    };
    Object.entries(labels).forEach(([selector, text]) => {
      document.querySelectorAll(selector).forEach((span) => {
        span.textContent = text;
      });
    });
  }

  /**
   * Part 2: conditional redirect for Addison Lee customers.
   *
   * When a checkout button is clicked and something blocks the click (Pickeasy does
   * this when no Pickeasy date is chosen), check the basket. If an Addison Lee slot
   * is booked, go to checkout, which is what Addison Lee's own redirect was for.
   * In every other case do nothing, so collection and Nationwide still go through the
   * normal form submit and Pickeasy saves its choice.
   *
   * - Runs after all other click listeners (setTimeout), so it doesn't matter which
   *   app blocked the click or in what order the listeners were added.
   * - Sends one read of the basket (/cart.js), only on a blocked checkout click.
   *   It never writes to the basket, so it can't start a request loop.
   *
   * Known gap: a customer who books Addison Lee AND picks a Pickeasy date ends up
   * with both choices in the basket, and checkout may offer no rate. Not handled here.
   */
  function listenForBlockedCourierCheckout() {
    let checking = false; // ignore repeat clicks while a check is in progress

    document.addEventListener(
      'click',
      (event) => {
        if (!event.target.closest || !event.target.closest('[name="checkout"]')) return;

        setTimeout(async () => {
          if (!event.defaultPrevented || checking) return; // checkout went ahead normally
          checking = true;
          try {
            const response = await fetch('/cart.js');
            const cart = await response.json();
            if (cart.attributes && cart.attributes['Addison Lee Timeslot']) {
              window.location.href = '/checkout';
            }
          } catch (error) {
            // Basket couldn't be read: do nothing, the customer stays in the basket.
          } finally {
            checking = false;
          }
        });
      },
      true
    );
  }
})();
